import { AlignJustify, AlignLeft, RotateCcw, X, Zap } from "lucide-react";
import type { CSSProperties } from "react";
import {
  type Display,
  type DisplayFace,
  FACES,
  isDefaultDisplay,
  LEADING_LABELS,
  SIZE_MAX,
  SIZE_MIN,
  WARMTH_MAX,
  WEIGHT_LABELS,
} from "./display";

/**
 * THE Aa SHEET (2026-09-13, the Kindle layer).
 *
 * Kindle's display settings in the order a Kindle reader knows them: the page colour first
 * (three tiles, each painted in its own theme with "Aa" set in the reading face, so the tile
 * IS the preview), then the typeface, then the size slider with a small A and a large A at its
 * ends, then boldness, line spacing, margins, alignment, and the Paperwhite's warm light. One
 * sheet, one scroll, one Reset at the foot that only appears once something has changed.
 *
 * The component owns no state: the App holds the Display object and the theme, and this is
 * the instrument panel for both. On a desktop it is the popover under the Aa on the masthead;
 * under 920px the existing `.view-menu.aa-pop` rule turns it into a bottom sheet.
 * House style: no em dashes or en dashes.
 */

export interface ThemeTile {
  id: string;
  label: string;
}

export default function DisplaySheet({
  display,
  onChange,
  onReset,
  theme,
  themes,
  onTheme,
  measure,
  onMeasure,
  bionic,
  onBionic,
  phone,
  onClose,
}: {
  display: Display;
  onChange: (patch: Partial<Display>) => void;
  onReset: () => void;
  theme: string;
  themes: readonly ThemeTile[];
  onTheme: (id: string) => void;
  measure: "comfy" | "wide";
  onMeasure: (m: "comfy" | "wide") => void;
  bionic: boolean;
  onBionic: () => void;
  /** A phone in a browser: the column control is meaningless there and is left out. */
  phone: boolean;
  onClose: () => void;
}) {
  const changed = !isDefaultDisplay(display);
  return (
    <div className="view-menu aa-pop display-sheet" role="dialog" aria-label="Display settings">
      <header className="ds-head">
        <span className="ds-title">Display</span>
        <button type="button" className="ds-close" onClick={onClose} aria-label="Close display settings">
          <X size={16} aria-hidden="true" />
        </button>
      </header>

      {/* 1. The page. Each tile wears its own theme so the choice is seen, not named. */}
      <div className="ds-themes" role="radiogroup" aria-label="Page colour">
        {themes.map((t) => (
          <button
            key={t.id}
            type="button"
            role="radio"
            aria-checked={theme === t.id}
            className={`ds-theme tile-${t.id}${theme === t.id ? " active" : ""}`}
            onClick={() => onTheme(t.id)}
          >
            <span className="ds-theme-aa" aria-hidden="true">Aa</span>
            <span className="ds-theme-name">{t.label}</span>
          </button>
        ))}
      </div>

      {/* 2. The typeface, each name set in itself. */}
      <section className="ds-row" aria-label="Typeface">
        <span className="ds-label">Font</span>
        <div className="ds-faces">
          {FACES.map((f) => (
            <button
              key={f.id}
              type="button"
              className={`ds-face face-${f.id}${display.face === f.id ? " on" : ""}`}
              aria-pressed={display.face === f.id}
              title={f.note}
              onClick={() => onChange({ face: f.id as DisplayFace })}
            >
              {f.label}
            </button>
          ))}
        </div>
      </section>

      {/* 3. Size: the slider with the two A's, Kindle's oldest instrument. */}
      <section className="ds-row" aria-label="Text size">
        <span className="ds-label">Size</span>
        <div className="ds-slider">
          <span className="ds-a ds-a-sm" aria-hidden="true">A</span>
          <input
            type="range"
            min={SIZE_MIN}
            max={SIZE_MAX}
            step={1}
            value={display.size}
            aria-label="Text size"
            aria-valuetext={`Size ${display.size} of ${SIZE_MAX}`}
            onChange={(e) => onChange({ size: Number(e.currentTarget.value) })}
          />
          <span className="ds-a ds-a-lg" aria-hidden="true">A</span>
        </div>
      </section>

      {/* 4. Boldness. */}
      <section className="ds-row" aria-label="Boldness">
        <span className="ds-label">Weight</span>
        <div className="ds-seg" role="radiogroup">
          {WEIGHT_LABELS.map((label, i) => (
            <button
              key={label}
              type="button"
              role="radio"
              aria-checked={display.weight === i}
              className={display.weight === i ? "on" : ""}
              style={{ fontWeight: 400 + i * 100 } as CSSProperties}
              onClick={() => onChange({ weight: i as 0 | 1 | 2 })}
            >
              {label}
            </button>
          ))}
        </div>
      </section>

      {/* 5. Line spacing, drawn as the lines themselves. */}
      <section className="ds-row" aria-label="Line spacing">
        <span className="ds-label">Spacing</span>
        <div className="ds-seg ds-lead" role="radiogroup">
          {LEADING_LABELS.map((label, i) => (
            <button
              key={label}
              type="button"
              role="radio"
              aria-checked={display.leading === i}
              aria-label={`${label} line spacing`}
              className={display.leading === i ? "on" : ""}
              onClick={() => onChange({ leading: i as 0 | 1 | 2 })}
            >
              <i className={`lead-${i}`} aria-hidden="true" />
            </button>
          ))}
        </div>
      </section>

      {/* 6. Margins: Kindle's word for how wide the column runs. A phone has one width. */}
      {!phone ? (
        <section className="ds-row" aria-label="Column width">
          <span className="ds-label">Margins</span>
          <div className="ds-seg" role="radiogroup">
            <button type="button" role="radio" aria-checked={measure === "comfy"} className={measure === "comfy" ? "on" : ""} onClick={() => onMeasure("comfy")}>
              Book
            </button>
            <button type="button" role="radio" aria-checked={measure === "wide"} className={measure === "wide" ? "on" : ""} onClick={() => onMeasure("wide")}>
              Wide
            </button>
          </div>
        </section>
      ) : null}

      {/* 7. Alignment. Justified text is the Kindle default; here it is offered, not imposed. */}
      <section className="ds-row" aria-label="Alignment">
        <span className="ds-label">Align</span>
        <div className="ds-seg ds-align" role="radiogroup">
          <button type="button" role="radio" aria-checked={display.align === "left"} aria-label="Align left" className={display.align === "left" ? "on" : ""} onClick={() => onChange({ align: "left" })}>
            <AlignLeft size={15} aria-hidden="true" />
          </button>
          <button type="button" role="radio" aria-checked={display.align === "justify"} aria-label="Justify" className={display.align === "justify" ? "on" : ""} onClick={() => onChange({ align: "justify" })}>
            <AlignJustify size={15} aria-hidden="true" />
          </button>
        </div>
      </section>

      {/* 8. Warm light, the Paperwhite's amber. Zero is off. */}
      <section className="ds-row" aria-label="Warm light">
        <span className="ds-label">Warmth</span>
        <div className="ds-slider ds-warm">
          <span className="ds-dot ds-dot-cool" aria-hidden="true" />
          <input
            type="range"
            min={0}
            max={WARMTH_MAX}
            step={1}
            value={display.warmth}
            aria-label="Warm light"
            aria-valuetext={display.warmth === 0 ? "Off" : `Warmth ${display.warmth} of ${WARMTH_MAX}`}
            onChange={(e) => onChange({ warmth: Number(e.currentTarget.value) })}
          />
          <span className="ds-dot ds-dot-warm" aria-hidden="true" />
        </div>
      </section>

      <button type="button" role="menuitemcheckbox" aria-checked={bionic} className={`view-item ds-bionic${bionic ? " on" : ""}`} onClick={onBionic}>
        <Zap size={14} aria-hidden="true" /> Bionic reading
      </button>

      {changed ? (
        <footer className="ds-foot">
          <button type="button" className="ds-reset" onClick={onReset}>
            <RotateCcw size={13} aria-hidden="true" /> Reset to the designed page
          </button>
        </footer>
      ) : null}
    </div>
  );
}
